import axios from 'axios';
import { BusinessCode, extractBusinessCode, extractData, isSuccessResponse } from './api.js';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

const AUTH_EVENT = 'ph-auth-changed';

/** 合并并发刷新，避免同一次导航内多次请求 /api/me */
let refreshInFlight = null;

function notifyAuthChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
  }
}

export function onAuthChanged(callback) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(AUTH_EVENT, callback);
  return () => window.removeEventListener(AUTH_EVENT, callback);
}

export function setAuth(token, user) {
  localStorage.setItem(TOKEN_KEY, token || '');
  localStorage.setItem(USER_KEY, JSON.stringify(user || null));
  notifyAuthChanged();
}

function setStoredUser(user) {
  if (!user) {
    localStorage.removeItem(USER_KEY);
  } else {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
  notifyAuthChanged();
}

/**
 * 用服务端 /api/me 校验 token 并刷新本地用户信息。
 * token 无效或用户不存在时清除本地登录态；网络错误时保留当前状态，避免误登出。
 * @returns {Promise<boolean>} 是否仍处于已登录且用户信息已更新
 */
export function refreshUserFromServer() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const token = getToken();
      if (!token) {
        if (getUser()) clearAuth();
        return false;
      }
      try {
        const res = await axios.get('/api/me');
        if (isSuccessResponse(res)) {
          const data = extractData(res);
          if (data) {
            setStoredUser({
              id: data.id,
              phone: data.phone || '',
              email: data.email || '',
            });
            return true;
          }
        }
        clearAuth();
        return false;
      } catch (err) {
        if (!err.response) {
          return !!getUser();
        }
        const status = err.response.status;
        const code = extractBusinessCode(err.response);
        if (
          status === 401 ||
          status === 404 ||
          [
            BusinessCode.UNAUTHORIZED,
            BusinessCode.TOKEN_INVALID,
            BusinessCode.TOKEN_EXPIRED,
            BusinessCode.TOKEN_MISSING,
            BusinessCode.NOT_FOUND,
          ].includes(code)
        ) {
          clearAuth();
          return false;
        }
        return !!getUser();
      }
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function getUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  notifyAuthChanged();
}

export function isLoggedIn() {
  return !!getToken();
}

/** 顶栏等展示用：优先手机号，否则邮箱 */
export function userDisplayName(user) {
  if (!user) return '';
  return user.phone || user.email || '';
}

export function buildAuthHeaders() {
  const token = getToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
  };
}
